import {
    FunctionComponent,
    useState,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { ethers } from 'ethers';
import {
    Button,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Center,
    Badge,
    Input,
    FormControl,
    FormLabel,
    FormErrorMessage,
    FormHelperText,
    Text,
} from '@chakra-ui/react';
import { downloadJSON } from '../../imports/utils';

type ExportWalletDialogProps = {
    colorScheme: string;
    wallet: any;
    onConfirm: () => void;
    onDiscard: () => void;
};

type PasswordStrength = 'weak' | 'medium' | 'strong';

const strongRegex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})',
);
const mediumRegex = new RegExp(
    '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})',
);

const PasswordStrengthTranslations = {
    weak: 'debole',
    medium: 'media',
    strong: 'forte',
};

const PasswordStrengthColors = {
    weak: 'red.500',
    medium: 'yellow.500',
    strong: 'green.500',
};

const getPasswordStrength = (password: string): PasswordStrength =>
    strongRegex.test(password)
        ? 'strong'
        : mediumRegex.test(password)
        ? 'medium'
        : 'weak';

const ExportWalletDialog: FunctionComponent<ExportWalletDialogProps> = ({
    colorScheme,
    children,
    wallet,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [encrypting, setEncrypting] = useState(false);

    const onClose = () => {
        setPassword('');
        setIsOpen(false);
    };
    const cancelRef = useRef(null);

    const handleConfirm = useCallback(async () => {
        setEncrypting(true);
        const encryptedJSON = await wallet.encrypt(password);
        downloadJSON(encryptedJSON, 'bishub_encrypted_wallet.json');
        setEncrypting(false);
        onClose();
    }, []);

    const handlePasswordChange = useCallback(
        e => setPassword(e.target.value),
        [],
    );

    const passwordStrength = getPasswordStrength(password);

    return (
        <>
            <Button colorScheme={colorScheme} onClick={() => setIsOpen(true)}>
                {children}
            </Button>

            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            Esportazione wallet
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Center>
                                <FormControl id='password'>
                                    <FormLabel>
                                        Inserisci una password per cifrare il
                                        wallet prima di scaricarlo
                                    </FormLabel>
                                    <Input
                                        type='password'
                                        onChange={handlePasswordChange}
                                        value={password}
                                    ></Input>
                                    <FormHelperText
                                        color={
                                            password
                                                ? PasswordStrengthColors[
                                                      passwordStrength
                                                  ]
                                                : 'blue.500'
                                        }
                                    >
                                        {!password &&
                                            'Il tuo wallet sarà cifrato utilizzando la tua password'}
                                        {password &&
                                            `Sicurezza: ${PasswordStrengthTranslations[passwordStrength]}`}
                                    </FormHelperText>
                                </FormControl>
                            </Center>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Annulla
                            </Button>
                            <Button
                                colorScheme='red'
                                onClick={handleConfirm}
                                ml={3}
                                isLoading={encrypting}
                                loadingText='Cifratura in corso'
                            >
                                Esporta
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default ExportWalletDialog;
